
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminStats } from '@/hooks/useAdminStats';
import { CoachStatistics } from '@/components/admin/CoachStatistics';
import { Users, Building2, Trophy, UserCheck, TrendingUp } from "lucide-react";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface SuperAdminHomepageProps {
  profile: Profile;
}

export const SuperAdminHomepage = ({ profile }: SuperAdminHomepageProps) => {
  const { stats, loading } = useAdminStats();

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="mb-12 animate-fade-in">
        <div className="flex items-center gap-4 mb-4">
          <div>
            <p className="text-gray-700 text-2xl font-semibold">
              Welcome back, {profile?.first_name}! Manage the entire system
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-6 md:grid-cols-4 mb-12">
        <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-white to-blue-50 border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
            <Users className="w-4 h-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {loading ? '-' : stats.totalUsers}
            </div>
            <div className="flex items-center text-sm text-blue-600">
              <TrendingUp className="w-3 h-3 mr-1" />
              <span>System wide</span>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-white to-purple-50 border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Clubs</CardTitle>
            <Building2 className="w-4 h-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {loading ? '-' : stats.totalClubs}
            </div>
            <div className="flex items-center text-sm text-purple-600">
              <TrendingUp className="w-3 h-3 mr-1" />
              <span>Active organizations</span>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-white to-green-50 border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Teams</CardTitle>
            <Trophy className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {loading ? '-' : stats.totalTeams}
            </div>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="w-3 h-3 mr-1" />
              <span>Across all clubs</span>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-white to-orange-50 border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Players</CardTitle>
            <UserCheck className="w-4 h-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {loading ? '-' : stats.totalPlayers}
            </div>
            <div className="flex items-center text-sm text-orange-600">
              <TrendingUp className="w-3 h-3 mr-1" />
              <span>Registered players</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coach Statistics Section */}
      <CoachStatistics />
    </div>
  );
};
